import React from "react";
import { Box, Stack } from "@mui/material";
import { FleetLocationFilterCards } from "../../../components/FleetManagement/FleetLocationFilterCards.partial";
import type { ScheduleToolbarProps } from "../../Schedule/partial/ScheduleToolbar.partial";
import { ScheduleColleagueSelect } from "../../Schedule/partial/ScheduleColleagueSelect.partial";
import { ScheduleModeToggle } from "../../Schedule/partial/ScheduleModeToggle.partial";
import {
  ScheduleResourceAutocomplete,
  type ScheduleResourceAutocompleteOption
} from "../../Schedule/partial/ScheduleResourceAutocomplete.partial";
import { ScheduleResourceBookingTypeControls } from "../../Schedule/partial/ScheduleResourceBookingTypeControls.partial";
import { dashboard2ScheduleFilterPanelSx } from "../dashboard2-status.styles";
import {
  isStandaloneResourceBookingActive,
  resourcePlaceBookingTypes,
  shouldShowResourceKindToggle
} from "../../Schedule/utils/resource-booking-schedule.utils";

export type Dashboard2ScheduleFilterPanelProps = Omit<
  ScheduleToolbarProps,
  "viewToggleSlot" | "multiDeleteSlot" | "scheduleViewMode" | "onScheduleViewModeChange"
>;

export const Dashboard2ScheduleFilterPanel: React.FC<Dashboard2ScheduleFilterPanelProps> = ({
  scheduleMode,
  onScheduleModeChange,
  isColleagueMode,
  isResourceMode,
  containerRef,
  tagLimit,
  colleagues,
  selectedColleagues,
  scheduleColleaguesIsFetching,
  storedScheduleColleaguesIsFetching,
  preStoredColleagues,
  isFirstAidSelected,
  onSelectedColleaguesChange,
  onStoreSelectedColleagues,
  resourceAvailableTypes,
  resourceBookingType,
  onResourceBookingTypeChange,
  onResourceFetchEnable,
  resourceAutocompleteLoading,
  bookableBookingObjectsError,
  resourceAutocompleteOptions,
  selectedResource,
  onSelectedResourceChange,
  resourceLocationCards = [],
  selectedResourceLocationIds = [],
  onToggleResourceLocation,
  onClearResourceLocationFilter,
  resourceKind = "equipment",
  onResourceKindChange
}) => {
  const placeBookingTypes = resourcePlaceBookingTypes(resourceAvailableTypes);
  const showResourceKindToggle = shouldShowResourceKindToggle(
    isResourceMode,
    resourceAvailableTypes,
    onResourceKindChange
  );
  const showResourceLocationFilter =
    isResourceMode &&
    isStandaloneResourceBookingActive(resourceBookingType) &&
    resourceLocationCards.length > 0 &&
    onToggleResourceLocation != null &&
    onClearResourceLocationFilter != null;

  return (
    <Stack spacing={showResourceLocationFilter ? 1.5 : 0} sx={{ width: "100%" }}>
      <Stack
        direction="row"
        flexWrap="wrap"
        alignItems="center"
        useFlexGap
        spacing={2}
        sx={dashboard2ScheduleFilterPanelSx}
        data-testid="dashboard2-schedule-filter-panel"
      >
        <ScheduleModeToggle
          scheduleMode={scheduleMode}
          setScheduleMode={onScheduleModeChange}
          compact
        />

        {isColleagueMode && colleagues.length >= 1 && (
          <ScheduleColleagueSelect
            containerRef={containerRef}
            tagLimit={tagLimit}
            colleagues={colleagues}
            selectedColleagues={selectedColleagues}
            scheduleColleaguesIsFetching={scheduleColleaguesIsFetching}
            storedScheduleColleaguesIsFetching={storedScheduleColleaguesIsFetching}
            preStoredColleagues={preStoredColleagues}
            isFirstAidSelected={isFirstAidSelected}
            setSelectedColleagues={onSelectedColleaguesChange}
            handleStoredScheduleColleagues={onStoreSelectedColleagues}
          />
        )}

        {isResourceMode && (
          <>
            <ScheduleResourceBookingTypeControls
              compact
              buttonStyle={{ fontSize: 24 }}
              iconSize="20px"
              placeBookingTypes={placeBookingTypes}
              resourceBookingType={resourceBookingType}
              resourceKind={resourceKind}
              showResourceKindToggle={showResourceKindToggle}
              onResourceBookingTypeChange={onResourceBookingTypeChange}
              onResourceKindChange={onResourceKindChange}
              onResourceFetchEnable={onResourceFetchEnable}
            />
            <ScheduleResourceAutocomplete
              isLoading={resourceAutocompleteLoading}
              loadError={bookableBookingObjectsError}
              options={resourceAutocompleteOptions}
              value={selectedResource}
              resourceBookingType={resourceBookingType}
              resourceKind={resourceKind}
              onResourceChange={(value: ScheduleResourceAutocompleteOption | null) => {
                onSelectedResourceChange(value);
              }}
            />
          </>
        )}
      </Stack>

      {showResourceLocationFilter && (
        <Box data-testid="dashboard2-resource-location-filter">
          <FleetLocationFilterCards
            displayMode="scroll"
            locations={resourceLocationCards}
            selectedLocationIds={selectedResourceLocationIds}
            onToggleLocation={onToggleResourceLocation}
            onClear={onClearResourceLocationFilter}
          />
        </Box>
      )}
    </Stack>
  );
};
